import clsx from "clsx";
import type { CampaignStageNode } from "../types";

const NODE_ICONS: Record<string, string> = {
  battle: "⚔️",
  elite: "💀",
  final_boss: "👑",
  boss: "👑",
  treasure: "💰",
  mystery: "❓",
  dead_end: "🚫",
};

const NODE_LABELS: Record<string, string> = {
  battle: "Fight",
  elite: "Elite",
  final_boss: "Boss",
  boss: "Boss",
  treasure: "Loot",
  mystery: "???",
  dead_end: "Trap",
};

interface CampaignMapProps {
  nodes: CampaignStageNode[];
  edges: { from: string; to: string }[];
  onSelectNode: (nodeId: string) => void;
  isLoading: boolean;
}

export function CampaignMap({ nodes, edges, onSelectNode, isLoading }: CampaignMapProps) {
  const rows = Array.from(new Set(nodes.map((n) => n.row))).sort((a, b) => a - b);
  const maxRow = rows.length ? rows[rows.length - 1] : 0;
  const byRow = (row: number) => nodes.filter((n) => n.row === row).sort((a, b) => a.col - b.col);

  return (
    <div className="flex flex-col items-stretch gap-1 py-2">
      {rows.map((row, rowIdx) => {
        const rowNodes = byRow(row);
        const maxCols = Math.max(...nodes.map((n) => n.col)) + 1;
        return (
          <div key={row} className="flex flex-col items-center">
            {row === maxRow && (
              <p className="text-[10px] text-gold-400 font-bold mb-1 tracking-wide">FINAL BOSS</p>
            )}
            {rowIdx > 0 && (
              <div className="flex justify-center gap-1 h-5 w-full mb-1">
                {rowNodes.map((node) => {
                  const parents = edges.filter((e) => e.to === node.id).map((e) => e.from);
                  const parentCleared = parents.some((pid) => nodes.find((n) => n.id === pid)?.cleared);
                  return (
                    <div
                      key={`link-${node.id}`}
                      className={clsx(
                        "w-0.5 h-full rounded-full",
                        parentCleared || node.cleared ? "bg-emerald-600/70" : "bg-gray-700",
                      )}
                      style={{ marginLeft: rowNodes.length > 1 ? `${(node.col / maxCols) * 40}%` : 0 }}
                    />
                  );
                })}
              </div>
            )}
            <div
              className="grid w-full gap-2 px-2"
              style={{ gridTemplateColumns: `repeat(${Math.max(rowNodes.length, 1)}, minmax(0, 1fr))` }}
            >
              {rowNodes.map((node) => (
                <CampaignNodeButton
                  key={node.id}
                  node={node}
                  disabled={isLoading || !node.reachable}
                  onClick={() => onSelectNode(node.id)}
                />
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function CampaignNodeButton({
  node,
  disabled,
  onClick,
}: {
  node: CampaignStageNode;
  disabled: boolean;
  onClick: () => void;
}) {
  const type = node.cleared ? "cleared" : node.display_type;
  const icon = node.cleared ? "✅" : NODE_ICONS[node.display_type] ?? "•";
  const label = node.cleared ? "Done" : NODE_LABELS[node.display_type] ?? node.display_type;

  return (
    <button
      type="button"
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
      className={clsx(
        "mx-auto w-14 h-14 rounded-full flex flex-col items-center justify-center border-2 transition-all",
        node.cleared && "bg-emerald-900/50 border-emerald-500 text-emerald-400",
        node.reachable &&
          !node.cleared &&
          "bg-kingdom-accent/20 border-kingdom-accent text-white animate-pulse-slow hover:bg-kingdom-accent/40 active:scale-95 cursor-pointer",
        !node.reachable && !node.cleared && "bg-gray-800 border-gray-600 text-gray-500 cursor-not-allowed opacity-60",
        disabled && node.reachable && "opacity-50 cursor-not-allowed",
      )}
      title={node.id}
    >
      <span className="text-lg">{icon}</span>
      <span className="text-[9px] mt-0.5 font-bold leading-none">{label}</span>
      {type === "mystery" && !node.cleared && (
        <span className="text-[7px] text-gray-400">hidden</span>
      )}
    </button>
  );
}
